import React, { createRef } from 'react';

// constants
import { ADD_CUSTOMER_TOOLTIP, BACK } from './constants';

import TopToolbar from '../global/subcomponents/topToolbar/TopToolbar';

import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import { Button, TextField } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import PhoneIcon from '@material-ui/icons/Phone';
import { AddAPhoto, Camera } from '@material-ui/icons';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import SmsIcon from '@material-ui/icons/Sms';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import debounce from 'lodash/debounce';
import './Customer.css';
import { BASE_URL, VERIFICATION, RULES } from '../global/Server/endpoints';
import BlacklistWarningPanel from './BlacklistWarningPanel';
import DuplicateCustomerPanel from './DuplicateCustomerPanel';

const DEFAULT_POINTS = 20;

class CustomerForm extends React.Component {
  constructor(props) {
    super(props);
    const isIOSSafari = /iP(ad|hone|od).*Safari/i.test(navigator.userAgent);
    this.videoRef = createRef();
    this.canvasRef = createRef();
    // Refs for the 6 verification-code boxes (auto-advance focus).
    this.codeRefs = Array.from({ length: 6 }, () => createRef());
    this.state = {
      firstName: '',
      lastName: '',
      dob: null,
      comments: '',
      phone: '',
      files: [],
      newFiles: [],
      capturedImageURL: null,
      showVideo: false,
      isCameraOpen: false,
      cameraFacingMode: 'environment',
      isMobile: window.matchMedia('(max-width: 800px)').matches || isIOSSafari,

      blacklistMatches: [],
      blacklistDismissed: false,

      duplicateNameMatches: [],
      duplicateNameDismissed: false,

      duplicatePhoneMatches: [],
      duplicatePhoneDismissed: false,

      saving: false,

      // ── Phone verification (NDS Phone Verification, Flow 2) ──────────
      // verifyStep: 'idle' → 'linkSent' → 'verified'. The customer accepts
      // consent + reveals the code on their OWN phone via the SMS link; the
      // employee just enters the 6-digit code they read out.
      verifyStep: 'idle',
      verifiedPhone: '', // the exact 10-digit value that was verified
      sendingLink: false,
      codeDigits: ['', '', '', '', '', ''],
      verifying: false,
      verifyError: '',
      resendIn: 0,
      // Whether this location requires phone verification (Manage Rule
      // toggle). Default true so we never let an unverified save slip
      // through before the rule loads; refined in componentDidMount.
      verificationRequired: true,
    };
  }

  componentDidMount() {
    // Load the location's "Require Phone Verification" rule so we can hide
    // the verification UI (and stop blocking Save) at locations that opt out.
    const locationId = this.props.user?.selectedLocationId;
    if (!locationId) return;
    this.authFetch(RULES.LIST_RULES, { locationId })
      .then((data) => {
        const rule = Array.isArray(data?.rules) ? data.rules[0] : null;
        if (rule && rule.requirePhoneVerification === false) {
          this.setState({ verificationRequired: false });
        }
      })
      .catch(() => {
        /* keep the safe default (required) on any error */
      });
  }

  componentWillUnmount() {
    if (this.videoRef.current && this.videoRef.current.srcObject) {
      const stream = this.videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
    }
    if (this.resendTimer) clearInterval(this.resendTimer);
  }

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  handleDateChange = (date, key) => {
    this.setState({ [key]: date });
  };

  handleNameChange = (e) => {
    const { name, value } = e.target;

    this.setState({ [name]: value, duplicateNameDismissed: false }, () => {
      const { firstName, lastName } = this.state;

      if (firstName && lastName) {
        this.debouncedCheckDuplicateNameMatches(firstName, lastName);
      }

      if (name === 'firstName' || name === 'lastName') {
        this.setState({ blacklistDismissed: false });
        this.debouncedCheckBlacklistMatch();
      }
    });
  };

  handlePhoneChange = (event) => {
    const { name, value } = event.target;
    let formattedValue = value.replace(/[^0-9]/g, '');
    if (formattedValue.length > 9) {
      formattedValue = formattedValue.substring(0, 10);
    }
    // Changing the phone after verification resets the verified status
    // (spec §3 step 5) — the new number hasn't been proven yet.
    const verificationReset =
      this.state.verifyStep !== 'idle' &&
      formattedValue !== this.state.verifiedPhone
        ? {
            verifyStep: 'idle',
            verifiedPhone: '',
            codeDigits: ['', '', '', '', '', ''],
            verifyError: '',
          }
        : {};

    this.setState(
      {
        [name]: formattedValue,
        duplicatePhoneDismissed: false,
        blacklistDismissed: false,
        ...verificationReset,
      },
      () => {
        this.debouncedCheckDuplicatePhone(formattedValue);
        this.debouncedCheckBlacklistMatch();
      }
    );
  };

  // ── Phone verification (Flow 2) ─────────────────────────────────────────
  authFetch = (endpoint, body) => {
    const token = localStorage.getItem('token');
    return fetch(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }).then((res) => res.json());
  };

  sendVerificationLink = async () => {
    const phone = (this.state.phone || '').replace(/\D/g, '');
    if (phone.length !== 10 || this.state.sendingLink) return;
    this.setState({ sendingLink: true, verifyError: '' });
    try {
      const data = await this.authFetch(VERIFICATION.SEND_LINK, {
        phone,
        locationId: this.props.user.selectedLocationId,
      });
      if (data.success) {
        this.setState({
          verifyStep: 'linkSent',
          codeDigits: ['', '', '', '', '', ''],
        });
        this.startResendCountdown();
        this.props.openFeedbackBar(
          'success',
          'Link sent. Ask the customer to accept consent on their phone, then read you the code.'
        );
      } else {
        this.props.openFeedbackBar(
          'error',
          data.message || 'Unable to send the link.'
        );
      }
    } catch (e) {
      this.props.openFeedbackBar('error', 'Network error. Please try again.');
    } finally {
      this.setState({ sendingLink: false });
    }
  };

  focusBox = (i) => {
    const ref = this.codeRefs[i];
    if (ref && ref.current) ref.current.focus();
  };

  handleCodeChange = (index, rawValue) => {
    const value = (rawValue || '').replace(/\D/g, '');
    const digits = [...this.state.codeDigits];
    if (value.length > 1) {
      // paste — fill from this box forward
      const chars = value.slice(0, 6 - index).split('');
      chars.forEach((c, i) => {
        digits[index + i] = c;
      });
      this.setState({ codeDigits: digits, verifyError: '' });
      this.focusBox(Math.min(index + chars.length, 5));
      return;
    }
    digits[index] = value;
    this.setState({ codeDigits: digits, verifyError: '' });
    if (value && index < 5) this.focusBox(index + 1);
  };

  handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !this.state.codeDigits[index] && index > 0) {
      this.focusBox(index - 1);
    }
  };

  verifyCode = async () => {
    const code = this.state.codeDigits.join('');
    const phone = (this.state.phone || '').replace(/\D/g, '');
    if (code.length !== 6 || this.state.verifying) return;
    this.setState({ verifying: true, verifyError: '' });
    try {
      const data = await this.authFetch(VERIFICATION.VERIFY_CODE, {
        phone,
        code,
        locationId: this.props.user.selectedLocationId,
      });
      if (data.verified) {
        if (this.resendTimer) clearInterval(this.resendTimer);
        this.setState({ verifyStep: 'verified', verifiedPhone: phone });
        this.props.openFeedbackBar('success', 'Phone verified successfully!');
      } else {
        this.setState({ verifyError: data.error || 'Incorrect code.' });
      }
    } catch (e) {
      this.setState({ verifyError: 'Network error. Please try again.' });
    } finally {
      this.setState({ verifying: false });
    }
  };

  resendVerification = async () => {
    if (this.state.resendIn > 0) return;
    const phone = (this.state.phone || '').replace(/\D/g, '');
    try {
      const data = await this.authFetch(VERIFICATION.RESEND_CODE, {
        phone,
        locationId: this.props.user.selectedLocationId,
      });
      if (data.success) {
        this.setState({
          codeDigits: ['', '', '', '', '', ''],
          verifyError: '',
        });
        this.startResendCountdown();
      } else {
        this.props.openFeedbackBar(
          'error',
          data.message || 'Please wait before requesting a new code.'
        );
        if (data.cooldownRemaining)
          this.startResendCountdown(data.cooldownRemaining);
      }
    } catch (e) {
      this.props.openFeedbackBar('error', 'Network error. Please try again.');
    }
  };

  startResendCountdown = (seconds = 60) => {
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.setState({ resendIn: seconds });
    this.resendTimer = setInterval(() => {
      this.setState((prev) => {
        if (prev.resendIn <= 1) {
          clearInterval(this.resendTimer);
          return { resendIn: 0 };
        }
        return { resendIn: prev.resendIn - 1 };
      });
    }, 1000);
  };

  checkBlacklistMatch = async () => {
    const firstName = (this.state.firstName || '').trim();
    const lastName = (this.state.lastName || '').trim();
    const phone = (this.state.phone || '').trim();

    const hasSearch =
      firstName.length >= 2 || lastName.length >= 2 || phone.length >= 2;
    if (!hasSearch) {
      this.setState({ blacklistMatches: [] });
      return;
    }

    try {
      const locationId = this.props.user?.selectedLocationId;
      const token = localStorage.getItem('token');

      const body = { locationId };
      if (firstName.length >= 2) body.firstName = firstName;
      if (lastName.length >= 2) body.lastName = lastName;
      if (phone.length >= 2) body.phone = phone;

      const response = await fetch(`${BASE_URL}/customer/blacklistSearch`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        this.setState({ blacklistMatches: data.customers || [] });
      }
    } catch (err) {
      // silent
    }
  };

  debouncedCheckBlacklistMatch = debounce(this.checkBlacklistMatch, 400);

  checkDuplicatePhone = (phone) => {
    const trimmed = (phone || '').replace(/\D/g, '');
    if (trimmed.length < 7) {
      this.setState({ duplicatePhoneMatches: [] });
      return;
    }
    try {
      const locationId = this.props.user.selectedLocationId;
      this.props.validateCustomerName(
        {
          type: 'phone',
          phone: trimmed,
          locationId,
          customerId: '',
        },
        (success, message, customers) => {
          this.setState({
            duplicatePhoneMatches: customers || [],
            duplicatePhoneDismissed: false,
          });
        }
      );
    } catch (err) {
      // silent
    }
  };

  checkDuplicateName = (firstName, lastName) => {
    if (!firstName?.trim() || !lastName?.trim()) {
      this.setState({ duplicateNameMatches: [] });
      return;
    }
    try {
      const locationId = this.props.user.selectedLocationId;
      this.props.validateCustomerName(
        {
          type: 'name',
          firstName,
          lastName,
          locationId,
          customerId: '',
        },
        (success, message, customers) => {
          this.setState({
            duplicateNameMatches: customers || [],
            duplicateNameDismissed: false,
          });
        }
      );
    } catch (err) {
      // silent
    }
  };

  debouncedCheckDuplicatePhone = debounce(this.checkDuplicatePhone, 400);

  debouncedCheckDuplicateNameMatches = debounce(this.checkDuplicateName, 400);

  // ── Camera ────────────────────────────────────────────────────────────────
  startWebcam = async () => {
    if (!this.videoRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (this.videoRef.current) {
        this.videoRef.current.srcObject = stream;
        this.videoRef.current.play();
      }
    } catch (err) {
      // silent
    }
  };

  handleTakePhotoClick = () => {
    this.setState(
      { showVideo: true, capturedImageURL: null, files: [], newFiles: [] },
      () => {
        this.startWebcam();
      }
    );
  };

  captureImage = () => {
    const canvas = this.canvasRef.current;
    const video = this.videoRef.current;
    if (canvas && video) {
      const width = video.videoWidth;
      const height = video.videoHeight;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, width, height);
      const imageURL = canvas.toDataURL('image/jpeg', 0.5);
      this.setState({ capturedImageURL: imageURL }, () => {
        this.stopCamera();
      });
      this.handleFileAdd(imageURL);
    }
  };

  stopCamera = () => {
    const stream = this.videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    this.setState({ showVideo: false });
  };

  handleFileAdd = async (fileDataURL) => {
    fetch(fileDataURL)
      .then((res) => res.blob())
      .then(async (blob) => {
        const file = new File(
          [blob],
          `${Date.now() + Math.floor(Math.random() * 10000)}.jpg`,
          { type: 'image/jpeg' }
        );
        const newFile = await this.getFormattedFileObject(file);

        this.setState((prevState) => {
          prevState.files.forEach((oldFile) => {
            if (oldFile?.isBlob && oldFile.localURL) {
              URL.revokeObjectURL(oldFile.localURL);
            }
          });
          return {
            files: [newFile],
            newFiles: [newFile],
            capturedImageURL: newFile.localURL,
          };
        });
      });
  };

  getFormattedFileObject = async (file) => {
    return {
      localURL: URL.createObjectURL(file),
      displayName: file.name,
      type: file.type,
      file,
    };
  };

  startCameraMobile = async () => {
    try {
      const existing = this.videoRef.current?.srcObject;
      if (existing) {
        existing.getTracks().forEach((track) => track.stop());
      }
      const constraints = {
        video: { facingMode: { ideal: this.state.cameraFacingMode } },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.setState({ isCameraOpen: true });
      if (this.videoRef.current) {
        this.videoRef.current.srcObject = stream;
      }
    } catch (err) {
      // silent
    }
  };

  switchCameraMobile = () => {
    const nextFacingMode =
      this.state.cameraFacingMode === 'environment' ? 'user' : 'environment';
    this.setState({ cameraFacingMode: nextFacingMode }, () => {
      if (this.state.isCameraOpen) {
        this.startCameraMobile();
      }
    });
  };

  stopCameraMobile = () => {
    const stream = this.videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    this.setState({ isCameraOpen: false });
  };

  capturePhotoMobile = () => {
    const canvas = this.canvasRef.current;
    const video = this.videoRef.current;
    if (canvas && video) {
      const size = Math.min(video.videoWidth, video.videoHeight);
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        video,
        (video.videoWidth - size) / 2,
        (video.videoHeight - size) / 2,
        size,
        size,
        0,
        0,
        size,
        size
      );
      const imageURL = canvas.toDataURL('image/jpeg', 0.8);
      this.setState({ capturedImageURL: imageURL }, () => {
        this.stopCameraMobile();
      });
      this.handleFileAdd(imageURL);
    }
  };

  handleOpenCameraMobile = () => {
    if (!this.state.isCameraOpen) {
      this.startCameraMobile();
    }
  };

  // ── Validation ────────────────────────────────────────────────────────────
  // A photo is mandatory on registration. We gate on `files` rather than
  // `capturedImageURL` because `files` is what actually gets uploaded —
  // handleFileAdd fills it asynchronously (data URL → blob → File), so
  // capturedImageURL is briefly set while there is still nothing to send.
  hasPhoto = () => this.state.files.length > 0;

  isSaveDisabled = () => {
    const { firstName, lastName, phone, saving, verifyStep, verifiedPhone } =
      this.state;
    if (saving) return true;
    if (!firstName || !lastName) return true;
    if (!phone || phone.length !== 10) return true;
    // Photo is required — no customer may be registered without one.
    if (!this.hasPhoto()) return true;
    // Phone must be verified before saving (spec §3) — and the verified
    // number must still match what's in the field. Skipped at locations
    // that have verification turned off in Manage Rule.
    if (
      this.state.verificationRequired &&
      (verifyStep !== 'verified' || verifiedPhone !== phone)
    )
      return true;
    return false;
  };

  // Tells the employee WHICH gate is still blocking the save, so a disabled
  // button is never a dead end. Phone verification wins when both are
  // outstanding: it's the slower round-trip (the customer has to open the
  // SMS link), so it's worth starting first.
  saveButtonLabel = () => {
    const { saving, phone, verifyStep, verificationRequired } = this.state;
    if (saving) return 'Saving...';
    const phonePending =
      verificationRequired && phone.length === 10 && verifyStep !== 'verified';
    if (phonePending) return 'Verify Phone to Save';
    if (!this.hasPhoto()) return 'Take Photo to Save';
    return 'Save Customer';
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  handleSave = () => {
    if (this.isSaveDisabled()) return;

    const {
      firstName,
      lastName,
      phone,
      dob,
      comments,
      files,
      newFiles,
      capturedImageURL,
    } = this.state;

    const customer = {
      locationId: this.props.user.selectedLocationId,
      customerId: '',
      firstName,
      lastName,
      dob,
      points: DEFAULT_POINTS,
      driverLicense: '',
      phone,
      address: '',
      comments,
      files,
      newFiles,
      isRule: false,
      maxMatchesPerDay: null,
      timeLimit: null,
      maxAmountPerDay: null,
      isBlocked: false,
      matchValues: [],
      userId: this.props.user.userId,
      employeeName: [this.props.user?.firstname, this.props.user?.lastname]
        .filter((p) => p && p !== 'null' && p !== 'undefined')
        .join(' '),
      capturedImageURL,
      walletType: 'apple',
    };

    this.setState({ saving: true });

    this.props.saveCustomer(
      customer,
      (data) => {
        this.setState({ saving: false });
        this.props.openFeedbackBar('success', 'Saved');
        if (this.props.onCustomerSaved) {
          this.props.onCustomerSaved(data?.customer || null);
        } else {
          this.props.handleEditingClear();
        }
      },
      (message) => {
        this.setState({ saving: false });
        this.props.openFeedbackBar('error', message);
      }
    );
  };

  // Inline verification UI shown directly below the Phone field once a valid
  // 10-digit number is entered (Flow 2). Hidden once verified — the green
  // "Verified" badge then lives on the phone field itself.
  renderVerification = () => {
    const {
      phone,
      verifyStep,
      sendingLink,
      codeDigits,
      verifying,
      verifyError,
      resendIn,
    } = this.state;

    // Location opted out of phone verification — no verification UI at all.
    if (!this.state.verificationRequired) return null;
    if (!phone || phone.length !== 10) return null;
    if (verifyStep === 'verified') return null;

    if (verifyStep === 'idle') {
      return (
        <Button
          className="cust-verify-send"
          startIcon={<SmsIcon />}
          onClick={this.sendVerificationLink}
          disabled={sendingLink}
          fullWidth
        >
          {sendingLink ? 'Sending…' : 'Send Verification Code to Customer'}
        </Button>
      );
    }

    return (
      <div className="cust-verify-box">
        <div className="cust-verify-head">
          <div className="cust-verify-icon">
            <SmsIcon />
          </div>
          <div>
            <div className="cust-verify-title">Code Sent to Customer!</div>
            <div className="cust-verify-sub">
              Ask the customer for the 6-digit code
            </div>
          </div>
        </div>

        <div className="cust-code-row">
          {codeDigits.map((d, i) => (
            <input
              key={i}
              ref={this.codeRefs[i]}
              className={`cust-code-input${d ? ' filled' : ''}`}
              type="tel"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => this.handleCodeChange(i, e.target.value)}
              onKeyDown={(e) => this.handleCodeKeyDown(i, e)}
            />
          ))}
        </div>

        {verifyError ? (
          <div className="cust-code-err">{verifyError}</div>
        ) : null}

        <Button
          className="cust-verify-confirm"
          onClick={this.verifyCode}
          disabled={verifying || codeDigits.join('').length !== 6}
          fullWidth
        >
          {verifying ? 'Verifying…' : 'Verify Code'}
        </Button>

        <button
          type="button"
          className={`cust-resend${resendIn > 0 ? ' disabled' : ''}`}
          onClick={this.resendVerification}
          disabled={resendIn > 0}
        >
          {resendIn > 0 ? `Wait ${resendIn}s to resend` : 'Resend Code'}
        </button>

        <div className="cust-verify-note">
          The customer received an SMS with a link to review &amp; accept the
          consent. Once they accept, their check-in code appears on their phone
          — ask them to read it to you and type it above.
        </div>
      </div>
    );
  };

  renderPhotoDesktop = () => {
    return (
      <div className="customer-photo-section">
        {this.state.showVideo ? (
          <video ref={this.videoRef} className="customer-photo-video" />
        ) : (
          <div className="customer-photo-circle">
            {this.state.capturedImageURL ? (
              <img
                src={this.state.capturedImageURL}
                alt="Captured"
                className="customer-photo-img"
              />
            ) : (
              <span className="customer-photo-placeholder">No Photo</span>
            )}
          </div>
        )}

        <canvas ref={this.canvasRef} style={{ display: 'none' }} />

        <div className="customer-photo-actions">
          {!this.state.showVideo && (
            <Button
              variant="contained"
              className="customer-pill-btn"
              onClick={this.handleTakePhotoClick}
              startIcon={<AddAPhoto />}
            >
              Open Camera
            </Button>
          )}
          {this.state.showVideo && (
            <Button
              variant="contained"
              className="customer-pill-btn"
              startIcon={<Camera />}
              onClick={this.captureImage}
            >
              Capture Image
            </Button>
          )}
        </div>

        {!this.state.showVideo && !this.hasPhoto() && (
          <div className="customer-photo-required">Photo required</div>
        )}
      </div>
    );
  };

  renderPhotoMobile = () => {
    return (
      <div className="customer-photo-section">
        {!this.state.isCameraOpen && (
          <div className="customer-photo-circle">
            {this.state.capturedImageURL ? (
              <img
                src={this.state.capturedImageURL}
                alt="Captured"
                className="customer-photo-img"
              />
            ) : (
              <span className="customer-photo-placeholder">No Photo</span>
            )}
          </div>
        )}

        {!this.state.isCameraOpen && (
          <div className="customer-photo-actions">
            <Button
              variant="contained"
              className="customer-pill-btn"
              onClick={this.handleOpenCameraMobile}
              startIcon={<AddAPhoto />}
            >
              Open Camera
            </Button>
          </div>
        )}

        {!this.state.isCameraOpen && !this.hasPhoto() && (
          <div className="customer-photo-required">Photo required</div>
        )}

        {this.state.isCameraOpen && (
          <div className="video-container">
            <video
              ref={this.videoRef}
              className="responsive-video"
              autoPlay
              playsInline
              muted
            />
            <button
              style={styles.captureButton}
              onClick={this.capturePhotoMobile}
            >
              <div style={styles.captureIcon}></div>
            </button>
            <button
              type="button"
              style={styles.flipButton}
              onClick={this.switchCameraMobile}
              aria-label="Switch Camera"
            >
              <FlipCameraIosIcon />
            </button>
          </div>
        )}

        <canvas ref={this.canvasRef} style={{ display: 'none' }} />
      </div>
    );
  };

  render() {
    return (
      <React.Fragment>
        <TopToolbar
          pageName={ADD_CUSTOMER_TOOLTIP}
          menuItems={[
            {
              title: BACK,
              icon: <KeyboardBackspaceIcon />,
              onClick: () => this.props.handleEditingClear(),
            },
          ]}
        />

        <div className="customer-add-wrapper">
          {this.state.isMobile
            ? this.renderPhotoMobile()
            : this.renderPhotoDesktop()}

          {!this.state.isCameraOpen && (
            <div className="customer-add-fields">
              {this.state.blacklistMatches.length > 0 &&
                !this.state.blacklistDismissed && (
                  <BlacklistWarningPanel
                    matches={this.state.blacklistMatches}
                    firstName={this.state.firstName}
                    onDismiss={() =>
                      this.setState({ blacklistDismissed: true })
                    }
                  />
                )}

              {this.state.duplicateNameMatches.length > 0 &&
                !this.state.duplicateNameDismissed && (
                  <DuplicateCustomerPanel
                    matches={this.state.duplicateNameMatches}
                    searchLabel={`${this.state.firstName.trim()} ${this.state.lastName.trim()}`}
                    matchType="name"
                    onDismiss={() =>
                      this.setState({ duplicateNameDismissed: true })
                    }
                  />
                )}

              {this.state.duplicatePhoneMatches.length > 0 &&
                !this.state.duplicatePhoneDismissed && (
                  <DuplicateCustomerPanel
                    matches={this.state.duplicatePhoneMatches}
                    searchLabel={this.state.phone}
                    matchType="phone"
                    onDismiss={() =>
                      this.setState({ duplicatePhoneDismissed: true })
                    }
                  />
                )}

              <TextField
                className="customer-add-input"
                variant="outlined"
                label="First Name"
                InputLabelProps={{ shrink: true }}
                name="firstName"
                value={this.state.firstName}
                onChange={this.handleNameChange}
                required
                fullWidth
              />

              <TextField
                className="customer-add-input"
                variant="outlined"
                label="Last Name"
                InputLabelProps={{ shrink: true }}
                name="lastName"
                value={this.state.lastName}
                onChange={this.handleNameChange}
                required
                fullWidth
              />

              <TextField
                className={`customer-add-input${
                  this.state.verifyStep === 'verified'
                    ? ' cust-phone-verified'
                    : ''
                }`}
                variant="outlined"
                label="Phone"
                InputLabelProps={{ shrink: true }}
                name="phone"
                value={this.state.phone}
                onChange={this.handlePhoneChange}
                disabled={this.state.verifyStep === 'verified'}
                required
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                  endAdornment:
                    this.state.verifyStep === 'verified' ? (
                      <InputAdornment position="end">
                        <span className="cust-verified-badge">
                          <CheckCircleIcon style={{ fontSize: 16 }} /> Verified
                        </span>
                      </InputAdornment>
                    ) : null,
                }}
              />

              {this.renderVerification()}

              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  autoOk
                  name="dob"
                  variant="inline"
                  inputVariant="outlined"
                  label="Date Of Birth"
                  className="customer-add-input"
                  format="MM/dd/yyyy"
                  placeholder="MM/DD/YYYY"
                  value={this.state.dob}
                  InputAdornmentProps={{ position: 'start' }}
                  onChange={(dateInput) => {
                    const date = new Date(dateInput?.toString());
                    if (date.toString() === 'Invalid Date') return;
                    this.handleDateChange(date, 'dob');
                  }}
                  fullWidth
                />
              </MuiPickersUtilsProvider>

              <TextField
                className="customer-add-input"
                variant="outlined"
                label="Comments"
                InputLabelProps={{ shrink: true }}
                name="comments"
                value={this.state.comments}
                onChange={this.handleChange}
                multiline
                minRows={4}
                maxRows={6}
                fullWidth
              />

              <div className="customer-add-info-banner">
                <InfoOutlinedIcon className="customer-add-info-icon" />
                <span>
                  Points, Match Rules, and Wallet Pass can be configured from
                  the customer profile after saving.
                </span>
              </div>

              <Button
                className="customer-primary-btn"
                onClick={this.handleSave}
                disabled={this.isSaveDisabled()}
                fullWidth
              >
                {this.saveButtonLabel()}
              </Button>
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }
}

const styles = {
  captureButton: {
    position: 'fixed',
    bottom: '120px',
    left: '50%',
    transform: 'translateX(-50%)',
    border: 'none',
    borderRadius: '50%',
    width: '70px',
    height: '70px',
    backgroundColor: '#fff',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0',
  },
  captureIcon: {
    width: '35px',
    height: '35px',
    borderRadius: '50%',
    backgroundColor: '#333',
  },
  flipButton: {
    position: 'fixed',
    bottom: '135px',
    right: '24px',
    border: 'none',
    borderRadius: '50%',
    width: '48px',
    height: '48px',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.3)',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0',
  },
};

export default CustomerForm;
